// Serial-govern running context (§ serial), built INCREMENTALLY. The front gives
// only the body (scenes + dialogue/action, speakers by name); it does NOT feed any
// pre-extracted assets/states. This module maintains a registry that starts EMPTY
// and grows episode by episode: the model, reading one episode's body plus the
// context-so-far, discovers entities (with 造型/looks + descriptions),
// merges identities, and states — per scene — who is present and in which 造型.
//
// Nothing here is seeded from the jsonl roster. render() shows only what has been
// discovered up to now (so episode 1 is small). The persistent core (entities +
// looks + per-scene refs) never compacts; the plot narrative (recent → synopsis →
// arc) ages down a ladder to stay within budget. The final v3 assets/scene-refs
// are built from THIS registry, not the roster.

const norm = (s: string): string => s.normalize("NFC").replace(/\s+/g, " ").trim();
// Strip an inline paren annotation from a name — `大牛(BigBull)` → `大牛`. The
// serial tool output is cleaned in toDelta, but names coming from the assembled
// BODY (scene cast, dialogue speakers written by normalize) still carry the source
// `中文（English）` dual-naming. resolveCanon runs those through this so a polluted
// speaker resolves against the clean registry (and, if unmatched, still ships
// clean). A leading paren (no name before it) is left whole.
const stripParen = (s: string): string => {
  const m = /[（(]/.exec(s);
  if (!m || m.index === 0) return s;
  return s.slice(0, m.index).trim();
};

// One 造型 (durable look): a canonical name, equivalent aliases (婚服=大红嫁衣), and
// a visual description for image generation.
export interface Look { name: string; aliases: string[]; description: string }
export interface DefaultLook { name: string; description: string }

// A registry entity, name-keyed (no provisional id — ids are minted at build time
// by assignProvisionalIds). Discovered incrementally by the serial pass.
export interface RegEntity {
  kind: "actor" | "location" | "prop";
  canonical: string;
  aliases: string[];
  description: string;
  looks: Look[];
  currentLook?: string; // canonical look name currently in effect (continuity)
  lastSeen: number; // last episode this entity was referenced (for render detail gating)
}

// A 造型 change the serial pass reports: from this scene on, `entity` (canonical)
// wears look `look` (a registered look档位). Sparse — only where a look CHANGES;
// finalize carries it forward until the next change. Presence/cast are NOT taken
// from the model — they come from the (name-corrected) body itself.
export interface LookChange { entity: string; look: string }

// A processed serial STEP — one window of 1+ contiguous episodes [startEp, endEp]
// parsed together in a single call. startEp === endEp when a window held one episode.
export interface RecentEpisode { startEp: number; endEp: number; summary: string; subEvents: string[] }
export const recentLabel = (r: { startEp: number; endEp: number }): string =>
  r.startEp === r.endEp ? `第${r.startEp}集` : `第${r.startEp}集–第${r.endEp}集`;
// One synopsis entry: a single aged-out episode (startEp === endEp) or an arc
// covering a contiguous range [startEp, endEp]. The range is tracked as numbers
// (not a concatenated label string) so re-folding yields a clean 第X集–第Y集
// instead of the old compounding 第1集–第10集–第19集… artifact.
export interface SynopsisLine { startEp: number; endEp: number; line: string }

export const synopsisLabel = (s: SynopsisLine): string =>
  s.startEp === s.endEp ? `第${s.startEp}集` : `第${s.startEp}集–第${s.endEp}集`;

export interface GlobalContext {
  entities: RegEntity[];
  lookChanges: Record<string, LookChange[]>; // scene_id → look changes at that scene (ordered)
  recent: RecentEpisode[];
  synopsis: SynopsisLine[];
}

// Per-window decisions the model emits. Identity work is split into two distinct
// acts (merging two entities is deliberate, not a side effect of listing aliases):
//   newEntities — declare a NEW entity with its canonical name + EVERY 叫法 seen
//     this window (aliases: translations, short/full names, past-life names, and
//     crucially the dialogue-speaker spellings — so body speakers resolve by alias
//     with no ghost asset). Re-declaring a known entity just tops up its aliases.
//   merges — a body 称呼 that is NOT yet registered is an alias of a known entity.
// applyDelta is CONSERVATIVE: neither act ever folds two already-registered
// entities. A name that belongs to another entity is skipped, never absorbed —
// wrongly merging two real characters is irreversible, a missed merge is a cheap
// manual fix.
export interface Delta {
  newEntities: Array<{
    name: string;
    kind: RegEntity["kind"];
    description: string;
    aliases: string[];
    defaultLook: DefaultLook;
  }>;
  merges: Array<{ from: string; into: string }>;
  looks: Array<{ entity: string; names: string[]; description: string }>; // names[0]=canonical
  lookChanges: Array<{ scene: string; entity: string; look: string }>; // sparse 造型 changes
  subEvents: string[];
  summary: string;
}

export const emptyDelta = (): Delta => ({ newEntities: [], merges: [], looks: [], lookChanges: [], subEvents: [], summary: "" });
export const emptyContext = (): GlobalContext => ({ entities: [], lookChanges: {}, recent: [], synopsis: [] });

export interface CompactOpts { recentEpisodes: number; synopsisChars: number; arcFold: number }
export const DEFAULT_COMPACT: CompactOpts = { recentEpisodes: 6, synopsisChars: 3500, arcFold: 10 };

// Serial windowing: pack contiguous episode bodies up to this token budget and
// parse them in ONE call. Context still flows window→window (serial), so a window
// only trades a little within-window pre-applied context for far fewer calls —
// the win is biggest late, where one big context is amortized over several episodes.
export const DEFAULT_WINDOW_TOKENS = 10000;

// Deterministic token estimate for CJK+latin mixed body text. Not a real
// tokenizer (grok/deepseek differ), but stable across runs so window boundaries —
// hence resume cache keys — are identical. CJK ≈ 0.7 tok/char, latin ≈ 0.25.
export function estimateTokens(text: string): number {
  let cjk = 0, total = 0;
  for (const ch of text) {
    total++;
    const c = ch.codePointAt(0)!;
    if ((c >= 0x3400 && c <= 0x9fff) || (c >= 0x3000 && c <= 0x30ff) || (c >= 0xff00 && c <= 0xffef) || (c >= 0xf900 && c <= 0xfaff)) cjk++;
  }
  return Math.round(cjk * 0.7 + (total - cjk) * 0.25);
}

// Greedy-pack episode token sizes into windows under `budget`. Episodes are atomic
// (cut only on episode boundaries); a single episode larger than the budget takes
// its own window. Returns inclusive 1-based [startEp, endEp] ranges. Pure +
// deterministic so resume re-packs identically.
export function packEpisodes(tokens: number[], budget: number = DEFAULT_WINDOW_TOKENS): Array<[number, number]> {
  const windows: Array<[number, number]> = [];
  let start = 0, acc = 0;
  for (let i = 0; i < tokens.length; i++) {
    if (i > start && acc + tokens[i]! > budget) { windows.push([start + 1, i]); start = i; acc = 0; }
    acc += tokens[i]!;
  }
  if (start < tokens.length) windows.push([start + 1, tokens.length]);
  return windows;
}

const KIND_LABEL: Record<RegEntity["kind"], string> = { actor: "人物", location: "场景", prop: "道具" };
const LABEL_KIND: Record<string, RegEntity["kind"]> = { 人物: "actor", 场景: "location", 地点: "location", 道具: "prop" };
export const kindFromLabel = (s: string): RegEntity["kind"] | undefined => LABEL_KIND[s.trim()];

export function findEntity(ctx: GlobalContext, name: string): RegEntity | undefined {
  const key = norm(name);
  return ctx.entities.find((e) => norm(e.canonical) === key || e.aliases.some((a) => norm(a) === key));
}

const addAlias = (e: RegEntity, name: string): void => {
  if (norm(name) !== norm(e.canonical) && !e.aliases.some((a) => norm(a) === norm(name))) e.aliases.push(name);
};

// Age/size modifier prefixes that a proper name would not carry — a name whose
// only extra over a shorter one is such a modifier is NOT "fuller" (年轻苏远桥 is
// not fuller than 苏远桥; 苏若雪 IS fuller than 若雪 because 苏 is a surname).
const MODIFIERS = ["年轻", "年老", "年幼", "年少", "中年", "青年", "少年", "老年", "幼年", "老", "小", "大", "少", "幼"];

// n properly extends m if it contains m and the leftover part is a real name
// fragment (a surname), not just a modifier.
function properlyExtends(n: string, m: string): boolean {
  const N = norm(n), M = norm(m);
  if (N === M || !N.includes(M)) return false;
  const extra = N.replace(M, "").trim();
  return extra.length > 0 && !MODIFIERS.some((mod) => extra === mod || extra.startsWith(mod));
}

// The "fullest" name in a set = the longest one that PROPERLY extends another
// (surname prefix, not a modifier). If none does, keep the first (current
// canonical) — avoids promoting a modified or unrelated long name.
export function fullest(names: string[]): string {
  const supers = names.filter((n) => names.some((m) => m !== n && properlyExtends(n, m)));
  return supers.length ? supers.reduce((a, b) => (b.length > a.length ? b : a)) : names[0]!;
}

// Crowd / collective names (同学们, 众人, 丫鬟们, 村民, 轿夫4名) are NOT single
// characters — nobody draws a portrait of "同学们". Such a name must never become an
// actor asset; its lines belong to a `group` speaker instead. Signal: ends with 们,
// is a bare crowd noun, has an explicit group qualifier/count, or ends in an
// inherently collective noun such as 群众/观众. A trailing count (轿夫4名 → 轿夫)
// is stripped first. Specific individual roles such as 传旨太监 remain actors.
const CROWD_WORDS = [
  "众人", "群众", "民众", "观众", "人群", "人们", "大家", "路人", "行人", "看客", "围观者",
  "村民", "士兵", "百姓", "学生", "同学", "丫鬟", "随从", "侍卫", "家丁", "仆人", "下人", "孩子",
  "轿夫", "护卫", "手下", "小弟", "喽啰", "官兵", "衙役", "太监", "宫女", "婢女", "商贩", "食客",
];
const COLLECTIVE_SUFFIXES = ["众人", "群众", "民众", "观众", "人群", "人们", "大家", "百姓", "官兵"];
const GROUP_QUALIFIER = /^(?:一群|一众|全体|所有|几名|数名|多名|[0-9零一二两三四五六七八九十百千]+名?)/;
export function isCrowdName(name: string): boolean {
  const n = norm(name).replace(/[0-9零一二两三四五六七八九十百千]+名?$/, "").trim();
  if (!n) return false;
  if (n.endsWith("们")) return true;
  if (CROWD_WORDS.includes(n)) return true;
  if (COLLECTIVE_SUFFIXES.some((word) => n.endsWith(word))) return true;
  return GROUP_QUALIFIER.test(n) && CROWD_WORDS.some((word) => n.endsWith(word));
}

// Canonical name for any name/alias (post-upsert), else the (paren-stripped) name.
// Body-sourced names (dialogue speakers, scene cast) resolve here — since the model
// declares every 叫法 (incl. speaker spellings) as an entity alias, findEntity picks
// them up; an unmatched name is the model having missed an alias, and ships clean.
export function resolveCanon(ctx: GlobalContext, name: string): string {
  const clean = stripParen(name);
  return findEntity(ctx, clean)?.canonical ?? clean;
}

// Canonical look name for a look name/alias on an entity, else the name unchanged.
export function resolveLook(e: RegEntity, name: string): string {
  const key = norm(name);
  return e.looks.find((l) => norm(l.name) === key || l.aliases.some((a) => norm(a) === key))?.name ?? name;
}

// The entity's baseline "常态" look name (everyday appearance) — the explicitly
// labelled one if present, else the first look reported. Scenes with no specific
// look fall back to this, so an ordinary scene never inherits a dramatic outfit.
const BASE_LABELS = ["常态", "日常", "平时", "基础", "普通"];
export function baseLook(e: RegEntity): string | undefined {
  if (e.looks.length === 0) return undefined;
  return (e.looks.find((l) => BASE_LABELS.some((b) => l.name.includes(b))) ?? e.looks[0]!).name;
}

// The registered entity (within kind) that a name belongs to, else undefined.
// exclude lets a caller ignore the entity it's currently updating.
function ownerOf(ctx: GlobalContext, kind: RegEntity["kind"], name: string, exclude?: RegEntity): RegEntity | undefined {
  const k = norm(stripParen(name));
  if (!k) return undefined;
  return ctx.entities.find((e) => e.kind === kind && e !== exclude && (norm(e.canonical) === k || e.aliases.some((a) => norm(a) === k)));
}

// Promote the fullest name (a superstring of another in the set) to canonical, so
// 苏若雪 wins over 若雪, 顾夜渊 over 夜渊.
function promoteCanonical(e: RegEntity): void {
  const all = [e.canonical, ...e.aliases];
  const best = fullest(all);
  if (norm(best) !== norm(e.canonical)) { e.canonical = best; e.aliases = all.filter((n) => norm(n) !== norm(best)); }
}

export function applyDelta(ctx: GlobalContext, delta: Delta, startEp: number, endEp: number = startEp): GlobalContext {
  // newEntities: declare/refresh an entity. An alias that already belongs to a
  // DIFFERENT entity is a cross-entity slip → skipped (never absorb another
  // character). Re-declaring a known name just tops up its aliases/description.
  for (const ne of delta.newEntities) {
    const canon = stripParen(ne.name);
    if (!canon) continue;
    const dst = ownerOf(ctx, ne.kind, canon);
    if (!dst) {
      const aliases: string[] = [];
      for (const a of ne.aliases) { const n = stripParen(a); if (n && norm(n) !== norm(canon) && !ownerOf(ctx, ne.kind, n)) aliases.push(n); }
      ctx.entities.push({
        kind: ne.kind,
        canonical: canon,
        aliases: [...new Set(aliases)],
        description: ne.description,
        looks: [{ name: ne.defaultLook.name, aliases: [], description: ne.defaultLook.description }],
        lastSeen: endEp,
      });
      continue;
    }
    for (const a of ne.aliases) { const n = stripParen(a); if (n && !ownerOf(ctx, ne.kind, n, dst)) addAlias(dst, n); }
    if (!dst.description && ne.description) dst.description = ne.description;
    const registeredDefault = dst.looks.find((look) => norm(look.name) === norm(ne.defaultLook.name));
    if (registeredDefault) registeredDefault.description = ne.defaultLook.description;
    else dst.looks.unshift({ name: ne.defaultLook.name, aliases: [], description: ne.defaultLook.description });
    promoteCanonical(dst);
  }

  // merges: a body 称呼 (`from`) that is NOT yet registered is an alias of `into`.
  // CONSERVATIVE: if `from` is already its own registered entity, skip — never fold
  // two registered characters (wrong merge is irreversible; a missed one is cheap).
  for (const { from, into } of delta.merges) {
    const intoCanon = stripParen(into);
    if (!intoCanon) continue;
    const dst = findEntity(ctx, intoCanon);
    if (!dst) continue;
    const f = stripParen(from);
    if (!f || norm(f) === norm(dst.canonical)) continue;
    if (findEntity(ctx, f)) continue; // `from` is a registered entity → don't fold
    addAlias(dst, f);
    promoteCanonical(dst);
  }

  for (const { entity, names: raw, description } of delta.looks) {
    const e = findEntity(ctx, entity);
    if (!e) continue;
    const names = raw.map((n) => n.trim()).filter(Boolean);
    if (!names.length) continue;
    const canonical = names[0]!;
    const existing = e.looks.find((l) => norm(l.name) === norm(canonical) || names.some((n) => l.aliases.some((a) => norm(a) === norm(n))));
    if (existing) {
      if (description) existing.description = description;
      for (const n of names) {
        if (norm(n) !== norm(existing.name) && !existing.aliases.some((a) => norm(a) === norm(n))) existing.aliases.push(n);
      }
    }
    else e.looks.push({ name: canonical, aliases: names.slice(1), description });
  }

  // Sparse 造型 changes → accumulate per scene; entity resolved to canonical. Also
  // stamp currentLook so the render gives the model each entity's latest 造型.
  for (const lc of delta.lookChanges) {
    const scene = lc.scene.trim();
    const entity = resolveCanon(ctx, lc.entity);
    const rawLook = lc.look.trim();
    if (!scene || !entity || !rawLook) continue;
    const registered = findEntity(ctx, entity);
    const look = registered ? resolveLook(registered, rawLook) : rawLook;
    (ctx.lookChanges[scene] ??= []).push({ entity, look });
    if (registered) registered.currentLook = look;
  }

  // Mark every entity referenced this episode as freshly seen (drives render
  // detail gating: recent entities render in full, dormant ones fold to one line).
  const touched = new Set<string>();
  for (const ne of delta.newEntities) { touched.add(ne.name); for (const a of ne.aliases) touched.add(a); }
  for (const mg of delta.merges) { touched.add(mg.from); touched.add(mg.into); }
  for (const l of delta.looks) touched.add(l.entity);
  for (const lc of delta.lookChanges) touched.add(lc.entity);
  for (const n of touched) { const e = findEntity(ctx, n); if (e) e.lastSeen = endEp; }

  ctx.recent.push({ startEp, endEp, summary: delta.summary, subEvents: delta.subEvents });
  return ctx;
}

const recentChars = (r: RecentEpisode): number => r.summary.length + r.subEvents.reduce((n, s) => n + s.length, 0);

// Age recent → synopsis one-liners (pure). Arc folding of the synopsis is a
// SEPARATE async step (pendingArcFold + applyArcFold) because it summarizes via
// LLM; keeping compact pure lets resume replay it deterministically.
export function compact(ctx: GlobalContext, opts: CompactOpts = DEFAULT_COMPACT): GlobalContext {
  while (ctx.recent.length > opts.recentEpisodes || (ctx.recent.length > 1 && ctx.recent.reduce((n, r) => n + recentChars(r), 0) > 6000)) {
    const oldest = ctx.recent.shift()!;
    if (oldest.summary.trim()) ctx.synopsis.push({ startEp: oldest.startEp, endEp: oldest.endEp, line: oldest.summary.trim() });
  }
  return ctx;
}

// The oldest `arcFold` synopsis entries to compress, if the synopsis is over its
// char budget; else null. Pure + deterministic, so the fold boundary — and thus
// the arc summary's cache key [startEp, endEp] — is identical across runs/resumes.
export function pendingArcFold(ctx: GlobalContext, opts: CompactOpts = DEFAULT_COMPACT): SynopsisLine[] | null {
  const total = ctx.synopsis.reduce((n, s) => n + s.line.length, 0);
  if (total <= opts.synopsisChars || ctx.synopsis.length <= opts.arcFold) return null;
  return ctx.synopsis.slice(0, opts.arcFold);
}

// Replace the oldest `arcFold` synopsis entries with one arc entry carrying the
// (LLM-produced) summary and the folded range's clean [startEp, endEp].
export function applyArcFold(ctx: GlobalContext, summary: string, opts: CompactOpts = DEFAULT_COMPACT): void {
  const fold = ctx.synopsis.splice(0, opts.arcFold);
  if (!fold.length) return;
  ctx.synopsis.unshift({ startEp: fold[0]!.startEp, endEp: fold[fold.length - 1]!.endEp, line: summary.trim() });
}

// Episodes of recency within which an entity renders in FULL detail; older
// (dormant) entities fold to a one-line stub so input doesn't grow with cast.
export const DETAIL_EPISODES = 6;

// Render the discovered-so-far context as md — BOTH the prompt and the per-step
// trace. Empty at episode 1. When `currentEpisode` is given, only entities seen
// within DETAIL_EPISODES render in full (简介 + 造型 + 描述); dormant ones fold to a
// one-line stub (name + aliases + current look) so identity is still recognizable
// on their return without bloating the input.
export function render(ctx: GlobalContext, currentEpisode?: number): string {
  const detailed = (e: RegEntity): boolean => currentEpisode === undefined || e.lastSeen >= currentEpisode - DETAIL_EPISODES;
  const out: string[] = ["# 设定"];
  for (const kind of ["actor", "location", "prop"] as const) {
    const list = ctx.entities.filter((e) => e.kind === kind);
    if (!list.length) continue;
    out.push(`## ${KIND_LABEL[kind]}`);
    for (const e of list) {
      const bits = [...(e.aliases.length ? [`别名 ${e.aliases.join("、")}`] : []), ...(e.currentLook ? [`当前造型 ${e.currentLook}`] : [])];
      out.push(`- ${e.canonical}｜${bits.join("｜")}`);
      if (!detailed(e)) continue; // dormant → one-line stub only
      if (e.description) out.push(`  简介：${e.description}`);
      for (const l of e.looks) {
        const nm = l.aliases.length ? `${l.name}(=${l.aliases.join("=")})` : l.name;
        out.push(`  造型 ${nm}${l.description ? `：${l.description}` : ""}`);
      }
    }
  }
  if (ctx.synopsis.length) { out.push("", "# 前情"); for (const s of ctx.synopsis) out.push(`- ${synopsisLabel(s)}：${s.line}`); }
  if (ctx.recent.length) {
    out.push("", "# 最近");
    for (const r of ctx.recent) {
      out.push(`## ${recentLabel(r)}`);
      if (r.summary.trim()) out.push(`梗概：${r.summary.trim()}`);
      for (const e of r.subEvents) out.push(`- ${e}`);
    }
  }
  return out.join("\n");
}
